import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import { MasterTableQuery } from '../../../models/system-settings/parking-master-data.model';

@Injectable({ providedIn: 'root' })
export class ApiParkingMasterDataService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  private get token(): string {
    return this.cookieService.get('token');
  }

  private tableParams(query: MasterTableQuery): Record<string, string | number> {
    const params: Record<string, string | number> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.searchText) params['searchText'] = query.searchText;
    if (query.siteId) params['siteId'] = query.siteId;
    if (query.startDate) params['startDate'] = query.startDate;
    if (query.endDate) params['endDate'] = query.endDate;
    return params;
  }

  getParkingVehicleTypeForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingVehicleTypeForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingVehicleTypeForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingVehicleTypeById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingVehicleTypeById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingVehicleTypeById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  insertParkingVehicleType(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingVehicleType', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingVehicleType',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingVehicleType(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingVehicleType/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingVehicleType',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getParkingCardTypeForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingCardTypeForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingCardTypeForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingCardTypeById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingCardTypeById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingCardTypeById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  insertParkingCardType(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingCardType', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingCardType',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingCardType(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingCardType/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingCardType',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getParkingCardModeForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingCardModeForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingCardModeForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingCardModeById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingCardModeById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingCardModeById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  insertParkingCardMode(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingCardMode', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingCardMode',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingCardMode(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingCardMode/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingCardMode',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getParkingPaymentLogForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingPaymentLogForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingPaymentLogForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingPaymentLogById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingPaymentLogById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingPaymentLogById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  getParkingGateTypeForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingGateTypeForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingGateTypeForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingGateTypeById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingGateTypeById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingGateTypeById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  insertParkingGateType(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingGateType', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingGateType',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingGateType(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingGateType/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingGateType',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getParkingGateForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingGateForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingGateForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingGateById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingGateById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingGateById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  insertParkingGate(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingGate', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingGate',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingGate(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingGate/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingGate',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getParkingAreaForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingAreaForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingAreaForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingAreaById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingAreaById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingAreaById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  insertParkingArea(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingArea', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingArea',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingArea(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingArea/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingArea',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getParkingProviders(siteId?: number | null): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingProviders', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingProviders',
      token: this.token,
      headerOnly: false,
      params: siteId ? { siteId } : {},
    });
  }

  getParkingGateGroupForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingGateGroupForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingGateGroupForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingGateGroupById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingGateGroupById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingGateGroupById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  getParkingGatesForGroupOptions(siteId: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingGatesForGroupOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingGatesForGroupOptions',
      token: this.token,
      headerOnly: false,
      params: { siteId },
    });
  }

  insertParkingGateGroup(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingGateGroup', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingGateGroup',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingGateGroup(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingGateGroup/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingGateGroup',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getParkingRateMasterForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingRateForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingRateForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingRateMasterById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingRateById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingRateById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  getTaiwanDiscountOptions(): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getTaiwanDiscountOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getTaiwanDiscountOptions',
      token: this.token,
      headerOnly: false,
    });
  }

  insertParkingRateMaster(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingRate', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingRate',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingRateMaster(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingRate/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingRate',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getParkingMainRateForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingMainRateForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingMainRateForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingMainRateById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingMainRateById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingMainRateById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  insertParkingMainRate(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingMainRate', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingMainRate',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingMainRate(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingMainRate/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingMainRate',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getParkingSubRateForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingSubRateForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingSubRateForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingSubRateById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingSubRateById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingSubRateById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  getParkingSubRateOptions(): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingSubRateOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingSubRateOptions',
      token: this.token,
      headerOnly: false,
    });
  }

  insertParkingSubRate(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingSubRate', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingSubRate',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingSubRate(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingSubRate/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingSubRate',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getParkingPenaltyRateForTable(query: MasterTableQuery): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingPenaltyRateForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingPenaltyRateForTable',
      token: this.token,
      headerOnly: false,
      params: this.tableParams(query),
    });
  }

  getParkingPenaltyRateById(id: number): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingPenaltyRateById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingPenaltyRateById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  getParkingPenaltyRateOptions(): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingPenaltyRateOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingPenaltyRateOptions',
      token: this.token,
      headerOnly: false,
    });
  }

  getParkingMainRateOptions(): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getParkingMainRateOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingMainRateOptions',
      token: this.token,
      headerOnly: false,
    });
  }

  insertParkingPenaltyRate(body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>('/SystemSettings/insertParkingPenaltyRate', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingPenaltyRate',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateParkingPenaltyRate(id: number, body: unknown): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(`/SystemSettings/updateParkingPenaltyRate/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingPenaltyRate',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getTaiwanCarCategoryOptions(): Observable<unknown> {
    return this.apiMethodService.request<unknown>('/SystemSettings/getTaiwanCarCategoryOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getTaiwanCarCategoryOptions',
      token: this.token,
      headerOnly: false,
    });
  }
}
